import { ReactNode } from 'react';

import { cn } from '@/services/utils';

type BadgeVariant = 'info' | 'success' | 'error' | 'warning' | 'neutral';
type BadgeType = 'status' | 'count';

interface BadgeProps {
    variant?: BadgeVariant;
    type?: BadgeType;
    number?: ReactNode;
    className?: string;
    children?: ReactNode;
}

/**
 * Background token per variant — applied on the wrapper for the `status`
 * type. The `count` type uses a different solid-color treatment further down.
 */
const VARIANT_BG: Record<BadgeVariant, string> = {
    info: '!tw-bg-info-lighter',
    success: '!tw-bg-success-lighter',
    error: '!tw-bg-error-lighter',
    warning: '!tw-bg-warning-lighter',
    neutral: '!tw-bg-fill-secondary'
};

/** Foreground token for the title span (also used when no `number` is set). */
const VARIANT_TEXT: Record<BadgeVariant, string> = {
    info: '!tw-text-info-darker',
    success: '!tw-text-success-darker',
    error: '!tw-text-error-darker',
    warning: '!tw-text-warning-darker',
    neutral: '!tw-text-t-primary'
};

function Badge({
    variant = 'info',
    type = 'status',
    number,
    className,
    children
}: BadgeProps) {
    const isCount = type === 'count';

    const wrapperClasses = cn(
        'tw-inline-flex',
        '!tw-items-center',
        '!tw-justify-center',
        'tw-gap-1',
        'tw-m-0',
        'tw-rounded-full',
        'tw-align-top',
        isCount
            ? [
                'tw-min-w-[16px]',
                'tw-px-1',
                'tw-h-4',
                '!tw-text-t-xs',
                'tw-shrink-0',
                'tw-align-top'
            ]
            : [
                'tw-w-max',
                'tw-px-1.5',
                'tw-h-4',
                'lg:tw-px-2',
                'lg:tw-h-5',
                'tw-text-t-xs',
                'lg:tw-text-t-sm',
            ],
        // `count` keeps the existing solid-color treatment (white text on a
        // solid error/warning background) — used for notification counts.
        isCount && variant === 'error' && 'tw-bg-error tw-text-white',
        isCount && variant === 'warning' && 'tw-bg-warning tw-text-white',
        // `status` uses the per-span colour tokens defined above. The
        // background goes on the wrapper, the foreground on each span.
        !isCount && VARIANT_BG[variant],
        className
    );

    if (isCount) {
        return <div className={wrapperClasses}>{children}</div>;
    }

    return (
        <div className={wrapperClasses}>
            {number !== undefined && number !== null && (
                <span className={cn(VARIANT_TEXT[variant], 'tw-text-trim')}>{number}</span>
            )}
            <span className={cn(VARIANT_TEXT[variant], 'tw-text-trim')}>{children}</span>
        </div>
    );
}

export { Badge };
export type { BadgeProps, BadgeType, BadgeVariant };
